import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Product } from '@domain/product/models/Product';
import { APP_CONFIG } from '@config/app.config';
import { ProductSelectionWithQuantity } from './ProductSelectBottomSheet';

export interface ProductQuantitiesBottomSheetProps {
  products: Product[];
  title?: string;
  hint?: string;
  confirmButtonText?: (count: number) => string;
  onFinish: (selections: ProductSelectionWithQuantity[]) => void | Promise<void>;
  onBack: () => void;
  onClose: () => void;
}

const defaultConfirm = (count: number) =>
  `Confirm ${count} product${count !== 1 ? 's' : ''}`;

const ProductQuantitiesBottomSheet: React.FC<ProductQuantitiesBottomSheetProps> = ({
  products,
  title = 'Set quantities',
  hint,
  confirmButtonText = defaultConfirm,
  onFinish,
  onBack,
  onClose,
}) => {
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const q: Record<string, number> = {};
    products.forEach(p => {
      q[p.id] = 1;
    });
    return q;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setQuantity = (productId: string, value: number) => {
    setQuantities(prev => ({ ...prev, [productId]: Math.max(1, value) }));
  };

  const handleConfirm = async () => {
    const selections: ProductSelectionWithQuantity[] = products.map(product => ({
      product,
      quantity: quantities[product.id] ?? 1,
    }));
    setIsSubmitting(true);
    try {
      await Promise.resolve(onFinish(selections));
    } finally {
      setIsSubmitting(false);
    }
  };

  const n = products.length;

  return (
    <>
      <DialogHeader>
        <Stack direction="row" alignItems="center" width="100%" spacing={1}>
          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        <Stack spacing={2} sx={{ p: 4, pt: 2 }}>
          {hint ? (
            <Typography variant="body2" color="text.secondary">
              {hint}
            </Typography>
          ) : null}
          <Stack spacing={2}>
            {products.map(product => (
              <Stack
                key={product.id}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Avatar
                  variant="rounded"
                  src={product.image}
                  alt={product.title}
                  sx={{
                    width: 40,
                    height: 40,
                    border: 1,
                    borderColor: APP_CONFIG.ui.theme.colors.border,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="medium"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {product.title || 'Untitled'}
                  </Typography>
                  {product.ean ? (
                    <Typography variant="caption" color="text.secondary">
                      {product.ean}
                    </Typography>
                  ) : null}
                </Box>
                <TextField
                  type="number"
                  size="small"
                  label="Qty"
                  value={quantities[product.id] ?? 1}
                  onChange={e => setQuantity(product.id, parseInt(e.target.value, 10) || 1)}
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={n === 0 || isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : confirmButtonText(n)}
        </Button>
      </DialogActions>
    </>
  );
};

export default ProductQuantitiesBottomSheet;
